// AI Playbook Generator — full-height right-side sheet.
//
// Pick a saved profile/pack, queue a backend playbook generation job,
// stream progress, and on completion drop the user on the new
// playbook detail page. Mirrors AIGeneratorSheet's UX language so it
// feels native.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useUiStore } from "@/lib/store/uiStore";
import { api } from "@/lib/api";
import { aiApi, type AiProgress } from "@/lib/api/ai";

type Phase = "pick" | "thinking" | "done";

const STEPS = [
  "Loading profile context",
  "Drafting opening narrative",
  "Tailoring stories per persona",
  "Writing objection handlers",
  "Composing closing & next steps",
  "Polishing tone",
];

export function PlaybookGenSheet(): JSX.Element | null {
  const open = useUiStore((s) => s.playbookGenOpen);
  const setOpen = useUiStore((s) => s.setPlaybookGenOpen);
  const showToast = useUiStore((s) => s.showToast);
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>("pick");
  const [packId, setPackId] = useState<string>("");
  const [progress, setProgress] = useState<AiProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const { data: profiles = [], isLoading } = useQuery({
    queryKey: ["profiles"],
    queryFn: api.listProfiles,
    enabled: open,
  });

  useEffect(() => {
    if (!open) {
      setPhase("pick");
      setPackId("");
      setProgress(null);
      setError(null);
      abortRef.current?.abort();
      abortRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  if (!open) return null;

  const runGenerate = async (): Promise<void> => {
    if (!packId) return;
    setPhase("thinking");
    setError(null);
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    try {
      await aiApi.generatePlaybook(packId, {
        signal: abortRef.current.signal,
        onProgress: setProgress,
      });
      setPhase("done");
      showToast("Playbook ready");
      // Backend stores the playbook on the pack itself — surface it on
      // the playbooks list (which polls the pack list).
      setTimeout(() => {
        setOpen(false);
        navigate("/playbooks");
      }, 600);
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : "Generation failed";
      // Surface a useful explanation: the pack must have company,
      // products, and stories before the AI can write a playbook for
      // it. Custom AI-drafted packs sometimes ship without products
      // until the user saves the editor, which causes a 422 here.
      const msg = raw.includes("422")
        ? "This pack is missing one of company / products / stories. Open Pack editor, fill in the missing section, save, then retry."
        : raw.includes("503")
          ? "AI service unavailable right now (Anthropic upstream or rate limit). Try again in a minute."
          : raw;
      if (raw !== "Aborted") {
        setError(msg);
        showToast(`Playbook generation failed: ${msg}`);
      }
      setPhase("pick");
    }
  };

  // C8 (2026-04-20): Backdrop click is disabled during the "thinking"
  // phase so a misclick outside the sheet doesn't silently abort an
  // in-flight LLM call. The user retains the explicit Cancel button.
  const closeOnBackdrop = (): void => {
    if (phase === "thinking") return;
    setOpen(false);
  };

  return (
    <div
      onClick={closeOnBackdrop}
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(20,34,56,.45)",
        backdropFilter: "blur(4px)",
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="slide-in-right"
        style={{
          width: 620,
          maxWidth: "95vw",
          height: "100%",
          background: "var(--surface)",
          borderLeft: "1px solid var(--bd)",
          display: "flex",
          flexDirection: "column",
          boxShadow: "-24px 0 60px -20px rgba(3,45,96,.4)",
        }}
      >
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid var(--bd-2)",
            background: "linear-gradient(135deg, var(--eb-15), var(--eb-50))",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: "rgba(255,255,255,.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 22,
              }}
            >
              ✦
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                AI-generated playbook
              </div>
              <div style={{ fontSize: 11, color: "#AACBE2" }}>
                Pick a profile · Claude drafts the demo script.
              </div>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            style={{
              background: "rgba(255,255,255,.1)",
              border: 0,
              color: "white",
              width: 28,
              height: 28,
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 16,
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="thin-scroll" style={{ flex: 1, overflow: "auto", padding: 24 }}>
          {phase === "pick" && (
            <>
              <div className="label" style={{ marginBottom: 8 }}>
                Choose a profile
              </div>
              {isLoading ? (
                <div style={{ fontSize: 12, color: "var(--ink-3)" }}>Loading profiles…</div>
              ) : (
                <div style={{ display: "grid", gap: 8 }}>
                  {profiles.map((p) => {
                    const on = packId === p.id;
                    return (
                      <button
                        key={p.id}
                        onClick={() => setPackId(p.id)}
                        style={{
                          textAlign: "left",
                          padding: "12px 14px",
                          borderRadius: 10,
                          background: on ? "var(--cb-95)" : "white",
                          border: on ? "1px solid var(--eb-50)" : "1px solid var(--bd)",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <div
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 8,
                            background: "var(--cb-95)",
                            color: "var(--eb-50)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 16,
                            flexShrink: 0,
                          }}
                        >
                          ✦
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>
                            {p.name}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                            {p.industry ?? "—"}
                          </div>
                        </div>
                        {on && (
                          <span style={{ color: "var(--eb-50)", fontSize: 18 }}>✓</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {error && (
                <div
                  style={{
                    marginTop: 14,
                    padding: 12,
                    background: "#fdecec",
                    border: "1px solid #f5c5c5",
                    borderRadius: 8,
                    fontSize: 12,
                    color: "#7a1d1d",
                  }}
                >
                  {error}
                </div>
              )}
            </>
          )}

          {phase === "thinking" && (
            <div style={{ padding: "40px 0" }}>
              <div
                style={{
                  width: 72,
                  height: 72,
                  margin: "0 auto 24px",
                  borderRadius: 18,
                  background: "linear-gradient(135deg, var(--eb-15), var(--eb-50))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: 32,
                  animation: "aiGenPulse 1.2s ease-in-out infinite",
                }}
              >
                ✦
              </div>
              <style>{`
                @keyframes aiGenPulse {
                  0%, 100% { transform: scale(1); opacity: .9; }
                  50%      { transform: scale(1.06); opacity: 1; }
                }
              `}</style>
              <div style={{ textAlign: "center", fontSize: 14, fontWeight: 600 }}>
                Generating playbook…
              </div>
              {progress?.label && (
                <div style={{ textAlign: "center", fontSize: 11, color: "var(--ink-3)", marginTop: 6 }}>
                  {progress.label} · {Math.round(progress.pct)}%
                </div>
              )}
              <div style={{ maxWidth: 440, margin: "28px auto 0", display: "grid", gap: 8 }}>
                {STEPS.map((s, i) => {
                  const pct = progress?.pct ?? 0;
                  const target = Math.floor((pct / 100) * STEPS.length);
                  const done = i < target;
                  const active = i === target;
                  return (
                    <div
                      key={s}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 14px",
                        borderRadius: 8,
                        background: active ? "var(--cb-95)" : "transparent",
                        border: active ? "1px solid var(--cb-90)" : "1px solid transparent",
                      }}
                    >
                      <span
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 999,
                          flexShrink: 0,
                          background: done ? "var(--teal)" : active ? "#E8A23F" : "var(--bd)",
                          color: "white",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 10,
                          fontWeight: 700,
                        }}
                      >
                        {done ? "✓" : active ? "◇" : ""}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          color: done ? "var(--ink-3)" : active ? "var(--ink)" : "var(--ink-3)",
                          fontWeight: active ? 600 : 400,
                        }}
                      >
                        {s}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {phase === "done" && (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  margin: "0 auto 16px",
                  borderRadius: 999,
                  background: "var(--teal)",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 30,
                }}
              >
                ✓
              </div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Playbook ready</div>
              <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 4 }}>
                Opening playbooks…
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            padding: "14px 24px",
            borderTop: "1px solid var(--bd-2)",
            background: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {phase === "pick" && (
            <>
              <button onClick={() => setOpen(false)} className="btn ghost">
                Cancel
              </button>
              <button
                onClick={() => { void runGenerate(); }}
                disabled={!packId}
                className="btn primary lg"
                style={{ opacity: packId ? 1 : 0.5 }}
              >
                ✦ Generate playbook
              </button>
            </>
          )}
          {phase === "thinking" && (
            <>
              <button
                onClick={() => {
                  abortRef.current?.abort();
                  setPhase("pick");
                }}
                className="btn ghost"
              >
                Cancel
              </button>
              <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                Usually takes 30–60 seconds…
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
